import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { DealDto, FileDto, FilePurpose } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { Icon } from '../icons';
import { readUpload, saveBlob, sizeLabel } from '../files';
import { fmtDate } from '../format';
import { ErrorBanner, Pill } from '../ui';
import { ask, askText } from '../dialogs';
import { FilePreview } from './DocumentsPage';
import { WhatsAppButton } from '../whatsapp';

type Tone = 'good' | 'warn' | 'bad' | 'info' | 'soft';

/** A chi va un documento: parole semplici, così non si manda al cliente quello per il consulente. */
export function purposeInfo(p: FilePurpose | null | undefined, advisor: string | null): { label: string; group: string; hint: string; tone: Tone } {
  const who = advisor || 'il consulente';
  switch (p) {
    case 'client':
      return { label: 'Per il cliente', group: 'Da mandare al cliente', hint: 'Questi si possono mandare al cliente', tone: 'good' };
    case 'advisor':
      return {
        label: `Per ${who}`,
        group: `Da mandare a ${who}`,
        hint: `Servono a ${who} per la pratica: NON mandarli al cliente`,
        tone: 'info',
      };
    case 'internal':
      return { label: 'Solo per noi', group: 'Solo per noi', hint: 'Appunti e lavori interni: non vanno a nessuno', tone: 'soft' };
    case 'old':
      return { label: 'Vecchia versione', group: 'Vecchie versioni', hint: 'Superate da una versione più nuova: non mandarle a nessuno', tone: 'bad' };
    default:
      return { label: 'Da decidere', group: 'Da decidere', hint: 'Scegli a chi va con il menu a destra', tone: 'warn' };
  }
}

const ORDER: (FilePurpose | null)[] = ['client', 'advisor', null, 'internal', 'old'];

/**
 * Documenti della trattativa, divisi per chi li deve ricevere: cliente, consulente della pratica, solo per noi,
 * vecchie versioni. Si caricano con «Carica file» o trascinandoli qui sopra; un clic sul nome li apre.
 */
export function DealFiles({ ctx, dealId }: { ctx: Ctx; dealId: number }) {
  const { api } = ctx;
  const [files, setFiles] = useState<FileDto[] | null>(null);
  const [advisor, setAdvisor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<FileDto | null>(null);
  const [showOld, setShowOld] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const me = ctx.me.user;

  const load = useCallback(async () => {
    try {
      const [list, d] = await Promise.all([
        api.get<FileDto[]>(`/api/deals/${dealId}/files`),
        api.get<{ deal: DealDto }>(`/api/deals/${dealId}`).catch(() => null),
      ]);
      setFiles(list);
      if (d) setAdvisor(d.deal.advisorName);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [api, dealId]);

  useEffect(() => {
    void load();
  }, [load]);

  const upload = async (list: FileList | File[] | null) => {
    if (!list || list.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      for (const f of [...list]) {
        const u = await readUpload(f);
        await api.post(`/api/deals/${dealId}/files`, { name: u.name, mime: u.mime, contentBase64: u.contentBase64 });
      }
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  const download = async (f: FileDto) => {
    try {
      saveBlob(await api.download(`/api/files/${f.id}/download`), f.name);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const remove = async (f: FileDto) => {
    if (!(await ask(`Eliminare "${f.name}"?`))) return;
    try {
      await api.del(`/api/files/documents/${f.id}`);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const setPurpose = async (f: FileDto, purpose: FilePurpose | null) => {
    try {
      const u = await api.put<FileDto>(`/api/files/${f.id}/purpose`, { purpose });
      setFiles((l) => (l ?? []).map((x) => (x.id === u.id ? u : x)));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const editAdvisor = async () => {
    const v = await askText('Consulente della pratica', advisor ?? '', 'Chi riceve la versione per il Ministero (es. Ferrone). Lascia vuoto se non c’è.');
    if (v === null) return;
    try {
      const r = await api.put<DealDto>(`/api/deals/${dealId}/advisor`, { name: v.trim() || null });
      setAdvisor(r.advisorName);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const canDelete = (f: FileDto) => me.role === 'admin' || me.seeAll || f.uploadedBy === me.id;
  const groups = ORDER.map((p) => ({ p, list: (files ?? []).filter((f) => (f.purpose ?? null) === p) })).filter((g) => g.list.length > 0);

  return (
    <div
      className={`deal-files${over ? ' over' : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        void upload([...e.dataTransfer.files]);
      }}
    >
      <div className="deal-files-head">
        <div className="muted small">Documenti · divisi per chi li deve ricevere</div>
        <input ref={input} type="file" multiple hidden onChange={(e) => void upload(e.target.files)} />
        <button type="button" className="btn small" onClick={() => input.current?.click()} disabled={busy}>
          <Icon name="plus" size={14} /> {busy ? 'Caricamento…' : 'Carica file'}
        </button>
      </div>
      <div className="deal-advisor small">
        Consulente della pratica: <b>{advisor || 'nessuno'}</b>
        <button type="button" className="btn ghost small" onClick={() => void editAdvisor()} title="Chi riceve la versione per il Ministero">
          <Icon name="edit" size={13} /> Cambia
        </button>
      </div>
      <ErrorBanner message={error} onClose={() => setError(null)} />
      {!files ? (
        <div className="muted small">Caricamento…</div>
      ) : files.length === 0 ? (
        <div className="deal-files-empty muted small">Nessun documento. Trascina qui i file o usa «Carica file».</div>
      ) : (
        groups.map(({ p, list }) => {
          const info = purposeInfo(p, advisor);
          const hidden = p === 'old' && !showOld;
          return (
            <div key={p ?? 'none'} className={`file-group ${info.tone}`}>
              <div className="file-group-head">
                <Pill tone={info.tone}>{info.group}</Pill>
                <span className="muted small">{info.hint}</span>
                {p === 'old' && (
                  <button type="button" className="btn ghost small" onClick={() => setShowOld((s) => !s)}>
                    {showOld ? 'Nascondi' : `Mostra (${list.length})`}
                  </button>
                )}
              </div>
              {!hidden && (
                <div className="ag-list">
                  {list.map((f) => (
                    <div key={f.id} className={`ag-row${p === 'old' ? ' file-old' : ''}`} style={{ cursor: 'default' }}>
                      <Icon name="paperclip" size={15} />
                      <span className="ag-title doc-open" onClick={() => setPreview(f)} title="Apri">
                        <b>{f.name}</b>
                        <div className="muted small">
                          {sizeLabel(f.size)} · {f.uploadedByName} · {fmtDate(f.createdAt)}
                        </div>
                      </span>
                      <select
                        className="file-purpose"
                        value={f.purpose ?? ''}
                        title="A chi va questo documento"
                        onChange={(e) => void setPurpose(f, (e.target.value || null) as FilePurpose | null)}
                      >
                        <option value="client">Per il cliente</option>
                        <option value="advisor">Per {advisor || 'il consulente'}</option>
                        <option value="internal">Solo per noi</option>
                        <option value="old">Vecchia versione</option>
                        <option value="">Da decidere</option>
                      </select>
                      <button type="button" className="btn ghost" onClick={() => void download(f)} title="Scarica">
                        <Icon name="download" size={15} />
                      </button>
                      <WhatsAppButton
                        ctx={ctx}
                        file={{ id: f.id, name: f.name, mime: f.mime, purpose: f.purpose }}
                        prefer={f.purpose === 'advisor' ? advisor : null}
                        advisor={advisor}
                      />
                      {canDelete(f) && (
                        <button type="button" className="btn ghost" onClick={() => void remove(f)} title="Elimina">
                          <Icon name="trash" size={15} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })
      )}
      {preview && <FilePreview ctx={ctx} file={preview} onClose={() => setPreview(null)} onDownload={() => void download(preview)} />}
    </div>
  );
}
